
import { supabase } from "@/integrations/supabase/client";
import { Node, Edge } from "@xyflow/react";
import { toast } from "@/hooks/use-toast";

export interface WhiteboardVersion {
  id: string;
  whiteboard_id: string;
  user_id: string;
  nodes_data: Node[];
  edges_data: Edge[];
  version_name?: string;
  is_auto_save: boolean;
  created_at: string;
}

export const supabaseWhiteboardService = {
  // Save whiteboard to database
  async saveWhiteboard(
    whiteboardId: string,
    nodes: Node[],
    edges: Edge[],
    isAutoSave: boolean = true,
    versionName?: string
  ): Promise<WhiteboardVersion | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (isAutoSave) {
          console.info('Autosave skipped: user not authenticated');
          return null;
        }
        toast({
          title: "Sign in required",
          description: "Please sign in to save your whiteboard to the cloud.",
          variant: "default",
        });
        return null;
      }

      const { data, error } = await supabase
        .from('whiteboard_versions')
        .insert({
          whiteboard_id: whiteboardId,
          user_id: user.id,
          nodes_data: nodes as any,
          edges_data: edges as any,
          is_auto_save: isAutoSave,
          version_name: versionName
        })
        .select()
        .single();

      if (error) throw error;
      
      // Cast the Json types back to Node[] and Edge[]
      return {
        ...data,
        nodes_data: data.nodes_data as unknown as Node[],
        edges_data: data.edges_data as unknown as Edge[]
      };
    } catch (error) {
      console.error('Failed to save whiteboard:', error);
      if (!isAutoSave) {
        toast({
          title: "Save Failed",
          description: "Could not save whiteboard to database",
          variant: "destructive",
        });
      }
      return null;
    }
  },

  // Load latest whiteboard version
  async loadWhiteboard(whiteboardId: string): Promise<WhiteboardVersion | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.info('Load skipped: user not authenticated');
        return null;
      }

      const { data, error } = await supabase
        .from('whiteboard_versions')
        .select('*')
        .eq('whiteboard_id', whiteboardId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      
      // Cast the Json types back to Node[] and Edge[]
      return {
        ...data,
        nodes_data: data.nodes_data as unknown as Node[],
        edges_data: data.edges_data as unknown as Edge[]
      };
    } catch (error) {
      console.error('Failed to load whiteboard:', error);
      return null;
    }
  },

  // Get all whiteboards for a user
  async getUserWhiteboards(): Promise<WhiteboardVersion[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.info('Get user whiteboards skipped: user not authenticated');
        return [];
      }

      const { data, error } = await supabase
        .from('whiteboard_versions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Cast the Json types back to Node[] and Edge[] for each item
      return (data || []).map(item => ({
        ...item,
        nodes_data: item.nodes_data as unknown as Node[],
        edges_data: item.edges_data as unknown as Edge[]
      }));
    } catch (error) {
      console.error('Failed to get user whiteboards:', error);
      return [];
    }
  },

  // Delete whiteboard
  async deleteWhiteboard(whiteboardId: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.info('Delete skipped: user not authenticated');
        return false;
      }

      const { error } = await supabase
        .from('whiteboard_versions')
        .delete()
        .eq('whiteboard_id', whiteboardId)
        .eq('user_id', user.id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Failed to delete whiteboard:', error);
      return false;
    }
  }
};
